import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import { toast } from "react-toastify";
import Select from "react-select";
import { addDesignation, updateDesignation } from "../AttendanceApis";

bouncy.register();

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const getResponseData = (responseOrError) =>
  responseOrError?.response?.data ??
  responseOrError?.data ??
  responseOrError;

const getBackendMessage = (responseOrError) => {
  const data = getResponseData(responseOrError);
  if (!data) return "";
  if (typeof data === "string") return data.trim();

  if (data.message || data.msg || data.Message || data.title) {
    return String(data.message || data.msg || data.Message || data.title).trim();
  }

  const modelErrors = data.errors || data.Errors;
  if (modelErrors && typeof modelErrors === "object") {
    const messages = Object.values(modelErrors)
      .flat()
      .filter(Boolean)
      .map(String);
    if (messages.length) return messages.join(" ");
  }

  if (Array.isArray(data) && data.length) {
    const first = data[0];
    return typeof first === "string"
      ? first
      : first?.message || first?.msg || "";
  }

  return "";
};

const isDuplicateMessage = (message = "") => /already exist/i.test(message);

const mapBackendFieldErrors = (responseOrError, setFieldError) => {
  const data = getResponseData(responseOrError);
  let mapped = false;

  const modelErrors = data?.errors || data?.Errors;
  if (modelErrors && typeof modelErrors === "object") {
    Object.entries(modelErrors).forEach(([key, value]) => {
      const message = Array.isArray(value) ? value[0] : value;
      if (!message) return;

      const field = String(key).toLowerCase();
      if (field.includes("designation")) {
        setFieldError("designationName", String(message));
        mapped = true;
      } else if (field.includes("department")) {
        setFieldError("departmentId", String(message));
        mapped = true;
      } else if (field.includes("isactive") || field.includes("status")) {
        setFieldError("isActive", String(message));
        mapped = true;
      }
    });
  }

  return mapped;
};

const isHttpErrorResponse = (responseOrError) => {
  if (!responseOrError) return true;
  if (responseOrError?.isAxiosError) return true;
  if (responseOrError?.response) return true;
  const status = responseOrError?.status;
  return typeof status === "number" && status >= 400;
};

const isSoftFailureResponse = (responseOrError) => {
  const data = getResponseData(responseOrError);
  const message = getBackendMessage(responseOrError);
  return (
    data?.result === 0 ||
    data?.result === false ||
    data?.success === false ||
    isDuplicateMessage(message)
  );
};

const isSuccessResponse = (responseOrError) => {
  if (isHttpErrorResponse(responseOrError)) return false;
  if (isSoftFailureResponse(responseOrError)) return false;

  const status = responseOrError?.status;
  const data = getResponseData(responseOrError);
  const hasSuccessStatus = status === 200 || status === 201;

  if (!hasSuccessStatus) return false;

  if (data?.result === 1 || data?.result === true || data?.success === true) {
    return true;
  }

  // Some create APIs return 200 without an explicit result flag
  return data?.result === undefined && data?.success === undefined;
};

const AddEditDesignations = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
  departmentData,
  getDesignationsData,
}) => {
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setShowModal(false);
  };

  const showBackendError = (responseOrError, setFieldError) => {
    const backendMessage = getBackendMessage(responseOrError);

    // Duplicate — toast only, no under-field message
    if (isDuplicateMessage(backendMessage)) {
      toast.error("Designation already exists");
      return;
    }

    const hasFieldError = mapBackendFieldErrors(
      responseOrError,
      setFieldError
    );

    if (hasFieldError) {
      return;
    }

    if (backendMessage) {
      toast.error(backendMessage);
      return;
    }

    toast.error(
      isEditMode
        ? "Failed to update designation. Please try again."
        : "Failed to create designation. Please try again."
    );
  };

  const handleSubmit = async (values, { setFieldError, setSubmitting }) => {
    const payload = {
      designationId: isEditMode ? editRow?.designationId ?? 0 : 0,
      designationName: values.designationName.trim(),
      departmentId: Number(values.departmentId),
      isActive: Number(values.isActive),
      remarks: "",
    };

    setLoading(true);
    try {
      const response = isEditMode
        ? await updateDesignation({
          ...payload,
          designationId: editRow?.designationId,
        })
        : await addDesignation(payload);

      if (isSuccessResponse(response)) {
        toast.success(
          isEditMode
            ? "Designation Updated Successfully"
            : "Designation Created Successfully"
        );
        handleClose();
        getDesignationsData();
        return;
      }

      showBackendError(response, setFieldError);
    } catch (err) {
      console.error("Designation save error:", err);
      showBackendError(err, setFieldError);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const initialValues = {
    departmentId: isEditMode
      ? editRow?.departmentId ?? editRow?.operationId ?? ""
      : "",
    designationName: isEditMode ? editRow?.designationName ?? "" : "",
    isActive: isEditMode
      ? String(
        editRow?.isActive === true || editRow?.isActive === 1 ? "1" : "0"
      )
      : "1",
  };

  const validationSchema = Yup.object().shape({
    departmentId: Yup.string().required("Department is required"),
    designationName: Yup.string()
      .trim()
      .required("Designation is required")
      .matches(/^[^\s]/, "Designation cannot start with a space.")
      .matches(
        /^[a-zA-Z0-9 ]+$/,
        "Designation cannot contain special characters."
      )
      .min(3, "Designation must contain at least 3 letters.")
      .max(100, "Designation must not exceed 100 characters"),
    isActive: Yup.string().required("Status is required"),
  });

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title id="contained-modal-title-vcenter">
          {isEditMode ? "Edit Designation" : "Add Designation"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
        ></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnBlur
          validateOnChange
        >
          {/* {({ setFieldValue, isSubmitting }) => (
            <FormikForm>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="departmentId" className="form-label">
                      Department <span className="text-danger">*</span>
                    </label>
                    <Field
                      as="select"
                      name="departmentId"
                      id="departmentId"
                      className="form-select"
                    >
                      <option value="">Select Department</option>
                      {departmentData
                        ?.filter((dept) => dept.isActive)
                        .map((dept) => (
                          <option
                            key={dept.departmentId ?? dept.operationId}
                            value={dept.departmentId ?? dept.operationId}
                          >
                            {dept.departmentName ?? dept.operationName}
                          </option>
                        ))}
                    </Field>
                    <ErrorMessage
                      name="departmentId"
                      component="div"
                      className="small required-message"
                      style={{ color: "red" }}
                    />
                  </div>
                </div> */}
          {({ setFieldValue, isSubmitting }) => (
            <FormikForm>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="departmentId" className="form-label">
                      Department <span className="text-danger">*</span>
                    </label>

                    <Field name="departmentId">
                      {({ field, form }) => (
                        <Select
                          inputId="departmentId"
                          name="departmentId"
                          placeholder="Select Department"
                          isSearchable
                          isClearable
                          options={departmentData
                            ?.filter((dept) => dept.isActive)
                            .map((dept) => ({
                              value: dept.departmentId ?? dept.operationId,
                              label: dept.departmentName ?? dept.operationName,
                            }))}
                          value={
                            departmentData
                              ?.filter((dept) => dept.isActive)
                              .map((dept) => ({
                                value: dept.departmentId ?? dept.operationId,
                                label: dept.departmentName ?? dept.operationName,
                              }))
                              .find(
                                (option) =>
                                  String(option.value) === String(field.value)
                              ) || null
                          }
                          onChange={(selectedOption) => {
                            form.setFieldValue(
                              "departmentId",
                              selectedOption ? selectedOption.value : ""
                            );
                          }}
                          onBlur={() =>
                            form.setFieldTouched("departmentId", true)
                          }
                          styles={{
                            control: (base) => ({
                              ...base,
                              backgroundColor: "#e9ecef",
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: "#fff",
                              zIndex: 9999,
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isFocused
                                ? "#f0f0f0"
                                : "#fff",
                              color: "#212529",
                            }),
                          }}
                        />
                      )}
                    </Field>

                    <ErrorMessage
                      name="departmentId"
                      component="div"
                      className="small required-message"
                      style={{ color: "red" }}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="designationName" className="form-label">
                      Designation <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="designationName"
                      id="designationName"
                      placeholder="Enter Designation"
                      className="form-control"
                      autoComplete="off"
                      maxLength={100}
                      onChange={(event) => {
                        const sanitizedValue = event.target.value.replace(
                          /[^a-zA-Z0-9 ]/g,
                          ""
                        );
                        setFieldValue(
                          "designationName",
                          sanitizedValue.slice(0, 100)
                        );
                      }}
                    />
                    <ErrorMessage
                      name="designationName"
                      component="div"
                      className="small required-message"
                      style={{ color: "red" }}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Status <span style={{ color: "#EC1117" }}>*</span>
                  </label>
                  <div
                    className="form-group"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div className="form-check d-flex">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="activeRadio"
                        value="1"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="activeRadio"
                      >
                        Active
                      </label>
                    </div>
                    <div className="form-check ms-4 d-flex">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="inactiveRadio"
                        value="0"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="inactiveRadio"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                  <ErrorMessage
                    name="isActive"
                    component="div"
                    className="small required-message"
                    style={{ color: "red" }}
                  />
                </div>
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  style={cancelButtonStyle}
                  onClick={handleClose}
                  disabled={loading || isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={saveButtonStyle}
                  disabled={loading || isSubmitting}
                >
                  {loading || isSubmitting ? (
                    <l-bouncy size="25" speed="1.75" color="white" />
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </FormikForm>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditDesignations;
